/* eslint-disable no-unused-vars */
import React, { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import toast from "react-hot-toast";
import { useCreateProductMutation } from "../Redux/api/productApi";
import { useUploadFileMutation } from "../Redux/api/mediaApi";
import { addProductSchema } from "../Validations/productValidation";
import { ImagePlus, X, Video, Image as ImageIcon } from "lucide-react";
import { motion } from "framer-motion";
import { useGetCategoriesQuery } from "../Redux/api/categoryApi";

const AddProductForm = ({ onClose, onAdded }) => {
  const [uploadedFiles, setUploadedFiles] = useState([]);

  const [uploadFile] = useUploadFileMutation();
  const [createProduct, { isLoading: isCreating }] = useCreateProductMutation();

  const { data: categories } = useGetCategoriesQuery();
  console.log("categories", categories);

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm({
    resolver: yupResolver(addProductSchema),
    defaultValues: {
      name: "",
      description: "",
      category_id: "",
      files: null,
      base_price: "",
      stock_quantity: "",
      min_order_quantity: "",
    },
  });

  const handleFileChange = async (files) => {
    if (!files || files.length === 0) return;

    const filesArray = Array.from(files);

    const initialStatus = filesArray.map((file) => ({
      file,
      previewUrl: URL.createObjectURL(file),
      status: "uploading",
      type: file.type.startsWith("video/") ? "video" : "image",
    }));

    setUploadedFiles((prev) => [...prev, ...initialStatus]);

    for (let i = 0; i < filesArray.length; i++) {
      try {
        const uploadRes = await uploadFile(filesArray[i]).unwrap();

        setUploadedFiles((prev) =>
          prev.map((item) =>
            item.file === filesArray[i] && item.status === "uploading"
              ? { ...item, status: "success", id: uploadRes.id }
              : item
          )
        );
      } catch (error) {
        setUploadedFiles((prev) =>
          prev.map((item) =>
            item.file === filesArray[i] && item.status === "uploading"
              ? { ...item, status: "error" }
              : item
          )
        );
        toast.error(`Failed to upload ${filesArray[i].name}`);
      }
    }
  };

  const handleRemoveFile = (index) => {
    setUploadedFiles((prev) => prev.filter((_, idx) => idx !== index));
  };

  const onSubmit = async (data) => {
    const fileIds = uploadedFiles
      .filter((item) => item.status === "success")
      .map((item) => item.id);

    if (fileIds.length === 0) {
      toast.error("Please upload at least one file successfully");
      return;
    }

    const toastId = toast.loading("Creating product...");

    try {
      const productPayload = {
        name: data.name,
        description: data.description,
        category_id: Number(data.category_id),
        file_ids: fileIds,
        base_price: Number(data.base_price),
        stock_quantity: Number(data.stock_quantity),
        min_order_quantity: Number(data.min_order_quantity),
      };

      const result = await createProduct(productPayload).unwrap();

      toast.dismiss(toastId);
      toast.success("Product added successfully!");
      reset();
      setUploadedFiles([]);
      onClose && onClose();
      onAdded && onAdded(result);
    } catch (err) {
      toast.dismiss(toastId);
      const errorMessage = err?.data?.message || "Failed to add product!";
      toast.error(errorMessage);
    }
  };

  return (
    <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Product Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          {...register("name")}
          placeholder="Enter product name"
          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#101b45] outline-none ${
            errors.name ? "border-red-500" : "border-gray-300"
          }`}
        />
        {errors.name && (
          <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Category <span className="text-red-500">*</span>
          </label>
          <select
            {...register("category_id")}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#101b45] outline-none ${
              errors.category_id ? "border-red-500" : "border-gray-300"
            }`}
          >
            <option value="">Select category</option>
            {categories.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.name}
              </option>
            ))}
          </select>
          {errors.category_id && (
            <p className="text-red-500 text-sm mt-1">
              {errors.category_id.message}
            </p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Price <span className="text-red-500">*</span>
          </label>
          <input
            type="number"
            step="0.01"
            {...register("base_price")}
            placeholder="Enter price"
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#101b45] outline-none ${
              errors.base_price ? "border-red-500" : "border-gray-300"
            }`}
          />
          {errors.base_price && (
            <p className="text-red-500 text-sm mt-1">
              {errors.base_price.message}
            </p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Min Order Quantity{" "}
            <span className="text-gray-400 font-medium">(Optional)</span>
          </label>
          <input
            type="number"
            {...register("min_order_quantity")}
            placeholder="Enter min order quantity"
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#101b45] outline-none ${
              errors.min_order_quantity ? "border-red-500" : "border-gray-300"
            }`}
          />
          {errors.min_order_quantity && (
            <p className="text-red-500 text-sm mt-1">
              {errors.min_order_quantity.message}
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Stock Quantity{" "}
            <span className="text-gray-400 font-medium">(Optional)</span>
          </label>
          <input
            type="number"
            {...register("stock_quantity")}
            placeholder="Enter stock quantity"
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#101b45] outline-none ${
              errors.stock_quantity ? "border-red-500" : "border-gray-300"
            }`}
          />
          {errors.stock_quantity && (
            <p className="text-red-500 text-sm mt-1">
              {errors.stock_quantity.message}
            </p>
          )}
        </div>
      </div>

      <div className="mb-5">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Upload Image/Video <span className="text-red-500">*</span>
        </label>

        <Controller
          control={control}
          name="files"
          render={({ field }) => (
            <div className="flex flex-col items-start gap-3">
              <label
                htmlFor="file-upload"
                className={`flex flex-col items-center justify-center w-full h-28 border-2 border-dashed rounded-2xl cursor-pointer transition-all duration-300
            ${
              errors.files
                ? "border-red-400 bg-red-50/40"
                : "border-gray-300 hover:border-[#101b45]"
            }`}
              >
                <ImagePlus className="w-7 h-7 text-gray-500 mb-2" />
                <p className="text-sm text-gray-600">
                  Click to{" "}
                  <span className="font-semibold text-[#101b45]">upload</span>{" "}
                  image or video
                </p>
                <input
                  id="file-upload"
                  type="file"
                  multiple
                  accept="image/*,video/*"
                  onChange={(e) => {
                    field.onChange(e.target.files);
                    handleFileChange(e.target.files);
                  }}
                  className="hidden"
                />
              </label>

              {uploadedFiles.length > 0 && (
                <div className="grid grid-cols-5 gap-3 w-full">
                  {uploadedFiles.map((item, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.3, delay: index * 0.1 }}
                      className="relative group"
                    >
                      <div className="relative w-full h-20 rounded-xl overflow-hidden shadow-md border border-gray-200">
                        {item.type === "video" ? (
                          <div className="w-full h-full bg-gray-900 flex items-center justify-center">
                            <Video className="w-12 h-12 text-white" />
                          </div>
                        ) : (
                          <img
                            src={item.previewUrl}
                            alt={`preview-${index}`}
                            className="w-full h-full object-cover"
                          />
                        )}

                        <div
                          className={`absolute top-2 left-2 px-1.5 py-1 rounded-lg text-[10px] font-semibold ${
                            item.status === "uploading"
                              ? "bg-yellow-400 text-yellow-900"
                              : item.status === "success"
                              ? "bg-green-400 text-green-900"
                              : "bg-red-400 text-red-900"
                          }`}
                        >
                          {item.status === "uploading"
                            ? "Uploading..."
                            : item.status === "success"
                            ? "✓ Uploaded"
                            : "✗ Failed"}
                        </div>

                        {item.status !== "uploading" && (
                          <button
                            type="button"
                            onClick={() => handleRemoveFile(index)}
                            className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>

                      <div className="absolute bottom-2 right-2">
                        {item.type === "video" ? (
                          <div className="bg-[#101b45] text-white p-1 rounded">
                            <Video className="w-2 h-2" />
                          </div>
                        ) : (
                          <div className="bg-green-500 text-white p-1 rounded">
                            <ImageIcon className="w-2 h-2" />
                          </div>
                        )}
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          )}
        />

        {errors.files && (
          <p className="text-red-500 text-sm mt-2">{errors.files.message}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Description{" "}
          <span className="text-gray-400 font-medium">(Optional)</span>
        </label>
        <textarea
          {...register("description")}
          placeholder="Write product description"
          className={`w-full h-[100px] px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#101b45] outline-none ${
            errors.description ? "border-red-500" : "border-gray-300"
          }`}
        />
        {errors.description && (
          <p className="text-red-500 text-sm mt-1">
            {errors.description.message}
          </p>
        )}
      </div>

      <div className="flex gap-3 justify-end pt-2">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={
            isCreating || uploadedFiles.some((f) => f.status === "uploading")
          }
          className={`px-4 py-2 bg-[#101b45] text-white rounded-lg hover:bg-[#101b45] transition ${
            isCreating || uploadedFiles.some((f) => f.status === "uploading")
              ? "opacity-50 cursor-not-allowed"
              : ""
          }`}
        >
          {isCreating
            ? "Creating Product..."
            : uploadedFiles.some((f) => f.status === "uploading")
            ? "Uploading Files..."
            : "Submit"}
        </button>
      </div>
    </form>
  );
};

export default AddProductForm;
